import { Menu, Shield } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useUiStore } from '@/store/ui.store';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { ROLES } from '@/lib/constants/app.constants';
import PepkorLogo from '@/assets/pepkor-logo.svg';

const ROLE_ID_TO_NAME: Record<string, string> = {
  [ROLES.SYSTEM_ADMIN.toUpperCase()]: 'System Admin',
  [ROLES.CARTON_INSPECTOR.toUpperCase()]: 'Carton Inspector',
  [ROLES.INSPECTOR.toUpperCase()]: 'Inspector',
  [ROLES.ANALYST.toUpperCase()]: 'Analyst',
  [ROLES.PENDING.toUpperCase()]: 'Pending',
  [ROLES.PLANNER.toUpperCase()]: 'Planner',
  [ROLES.QC_INSPECTOR.toUpperCase()]: 'QC Inspector',
  [ROLES.TECHNICIAN.toUpperCase()]: 'Technician',
  [ROLES.REVIEWER.toUpperCase()]: 'Reviewer',
  [ROLES.BOOKING_REQUESTOR.toUpperCase()]: 'Booking Admin',
  [ROLES.USER_ADMIN.toUpperCase()]: 'User Admin',
  [ROLES.DECISION_EDITOR.toUpperCase()]: 'Decision Editor',
};

const getRoleNames = (roleIds: string[] | undefined): string[] => {
  if (!roleIds || roleIds.length === 0) return [];
  return roleIds.map((id) => ROLE_ID_TO_NAME[id.toUpperCase()] || id).filter(Boolean);
};

export const TopNav = () => {
  const { user } = useAuth();
  const isMobile = useUiStore((state) => state.isMobile);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);

  const userRoleNames = getRoleNames(user?.roles);

  const getInitials = (name?: string, email?: string): string => {
    if (name) {
      // Extract first letter of first name and first letter of last name
      const nameParts = name.trim().split(/\s+/).filter(Boolean);
      if (nameParts.length >= 2) {
        const first = nameParts[0]?.charAt(0) || '';
        const last = nameParts[nameParts.length - 1]?.charAt(0) || '';
        return `${first}${last}`.toUpperCase();
      }
      // If only one name part, use first two letters
      if (nameParts.length === 1 && nameParts[0] && nameParts[0].length >= 2) {
        return nameParts[0].substring(0, 2).toUpperCase();
      }
      // If single character name, use it
      if (nameParts.length === 1 && nameParts[0]) {
        return nameParts[0].charAt(0).toUpperCase();
      }
    }
    // Fallback to email initial if no name
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <TooltipProvider>
      <header
        className="fixed right-0 top-0 z-40 h-[68px] border-b bg-white transition-all duration-500 ease-in-out"
        style={{ left: isMobile ? '0' : '60px' }}
      >
        <div className="flex h-full items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            {/* Hamburger Menu - Mobile/Tablet Only */}
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className="touch-target lg:hidden"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            )}

            {/* Client Logo - Responsive sizing */}
            <div className="flex items-center">
              <img
                src={PepkorLogo}
                alt="Pepkor"
                className="h-[20px] w-auto sm:h-[24px] lg:h-[30px]"
              />
            </div>
          </div>

          {/* User Avatar with Initials and Details Tooltip */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#6366F1] text-xs font-semibold text-white transition-opacity hover:opacity-90 sm:h-10 sm:w-10 sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:ring-offset-2"
                  aria-label="User details"
                >
                  {user ? getInitials(user.name, user.email) : 'U'}
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                sideOffset={8}
                className="p-3 bg-white border border-gray-200 shadow-lg max-w-xs"
              >
                {user ? (
                  <div>
                    {user.name && (
                      <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                    )}
                    <p className={`text-sm text-gray-500 ${user.name ? 'mt-1' : ''}`}>
                      {user.email}
                    </p>
                    {userRoleNames.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                          <Shield className="h-3 w-3" />
                          <span>Roles:</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {userRoleNames.map((role) => (
                            <span
                              key={role}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                            >
                              {role}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {userRoleNames.length === 0 && user.roles && user.roles.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                          <Shield className="h-3 w-3" />
                          <span>Role IDs:</span>
                        </div>
                        <div className="text-xs text-gray-400 break-all">
                          {user.roles.join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No user information available</p>
                )}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </header>
    </TooltipProvider>
  );
};
